import type { IdentityResponseGETAuthCurrent } from '@exylon/generated-contract-kernel/types';
import { createContext, useContext, useEffect, useMemo, useRef, useSyncExternalStore, type ReactNode } from 'react';

import { randomUuid } from '../../runtime/browser-crypto';
import { isCanonicalUuid } from '../../runtime/format';
import { scopeInstanceKey, useLiveRefresh } from '../../runtime/scoped-screen-runtime';
import type { PanelScreenConnection } from '../runtime-port';
import type { NoticeInboxPort, NoticeInboxScope } from './contract-port';
import { createSessionInboxController, type SessionInboxController, type SessionInboxSnapshot } from './inbox-controller';
import { NoticeList, type NoticeInboxProjection } from './notice-list';
import { PanelSessionInboxPresentationProvider } from './presentation';
import { NOTICE_INBOX_MAX_PAGES } from './read-model';

export interface SessionInboxProviderProps {
  readonly port: NoticeInboxPort;
  readonly session: IdentityResponseGETAuthCurrent['result'] | null;
  readonly principalKey: string;
  readonly sessionActive?: boolean;
  readonly connection?: PanelScreenConnection;
  readonly onNavigateHome?: () => void;
  readonly now?: () => number;
  readonly createIdempotencyKey?: () => string;
  readonly pollIntervalMs?: number;
  readonly children: ReactNode;
}

const SessionInboxContext = createContext<Readonly<{ controller: SessionInboxController; enabled: boolean }> | null>(null);

/** The session's one inbox, for a staff screen's corner; null outside a restaurant session (the TPV's offline door). */
export function useSessionInbox() {
  return useContext(SessionInboxContext);
}

/** Reports while the inbox's content is mounted, which is exactly while its dialog is open. */
function InboxPresence({ controller, children }: { readonly controller: SessionInboxController; readonly children: ReactNode }) {
  useEffect(() => {
    controller.inboxPresence(true);
    return () => controller.inboxPresence(false);
  }, [controller]);
  return <>{children}</>;
}

export function sessionInboxScope(session: SessionInboxProviderProps['session']): NoticeInboxScope | null {
  const restaurant = session?.restaurant;
  if (!restaurant || !restaurant.franchise_id || !session?.user || !isCanonicalUuid(restaurant.id)
    || !isCanonicalUuid(restaurant.franchise_id) || !isCanonicalUuid(session.user.id)) return null;
  if (session.session.membership_tenant_type !== 'restaurant' || session.session.membership_tenant_id !== restaurant.id) return null;
  return { tenantId: restaurant.franchise_id, restaurantId: restaurant.id, subjectUserId: session.user.id };
}

const NO_READ: SessionInboxSnapshot = { value: null, failure: null, staleAfterRefusal: false, pending: [], errors: {}, pages: 1 };
const noSubscription = () => () => undefined;

/** What the open inbox shows. A background refresh changes none of it until its answer lands. */
function projection(read: SessionInboxSnapshot, authorised: boolean, enabled: boolean): NoticeInboxProjection {
  const items = read.value?.window?.items ?? [];
  const complete = read.value?.window?.complete ?? false;
  const unreadCount = authorised ? read.value?.unreadCount ?? null : null;
  if (!authorised) return { status: 'error', items: [], complete: false, unreadCount, error: 'La sesión no permite leer este buzón.' };
  if (!enabled) return { status: 'error', items, complete: false, unreadCount, error: 'Reconectando. Se conserva la última lectura de tus avisos.' };
  switch (read.failure) {
    case 'authorization_denied': return { status: 'error', items: [], complete: false, unreadCount: null, error: 'No se ha autorizado la lectura de tu buzón.' };
    case 'invalid_response': return { status: 'error', items: [], complete: false, unreadCount: null, error: 'No se han podido cargar tus avisos.' };
    case 'transport': return { status: 'error', items, complete: false, unreadCount, error: 'No se han podido cargar tus avisos.' };
    case 'receipt_not_observed': return { status: 'error', items, complete: false, unreadCount, error: 'El acuse está confirmado, pero su lectura todavía no está actualizada.' };
    case null: break;
  }
  if (read.staleAfterRefusal) return { status: 'error', items, complete: false, unreadCount, error: 'Vuelve a leer tu buzón antes de continuar.' };
  return read.value?.window ? { status: 'ready', items, complete, unreadCount } : { status: 'loading', items: [], complete: false, unreadCount };
}

/** One controller per authenticated restaurant scope. Route changes do not remount it. */
export function SessionInboxProvider({
  port, session, principalKey, sessionActive = true, connection = 'online', now = Date.now, onNavigateHome,
  createIdempotencyKey = randomUuid, pollIntervalMs = 30_000, children,
}: SessionInboxProviderProps) {
  const scope = sessionInboxScope(session);
  const sessionCurrent = sessionActive && !!session && Number.isFinite(Date.parse(session.session.expires_at))
    && Date.parse(session.session.expires_at) > now();
  const authorised = !!scope && !!principalKey && sessionCurrent;
  const enabled = authorised && connection === 'online';
  // Read through a ref: a controller outlives renders, and the session can lapse between them.
  const admission = useRef<() => boolean>(() => false);
  admission.current = () => enabled && Date.parse(session?.session.expires_at ?? '') > now();
  const keyFactory = useRef(createIdempotencyKey);
  keyFactory.current = createIdempotencyKey;
  const controllerKey = JSON.stringify([scopeInstanceKey(port), principalKey, scope, session?.session.membership_version, session?.user.authority_version]);
  const controller = useMemo(() => scope === null || !principalKey ? null : createSessionInboxController({
    port, scope, enabled: () => admission.current(), createIdempotencyKey: () => keyFactory.current(),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one controller per key: a new scope object with the same key is the same inbox
  }), [controllerKey]);
  const read = useSyncExternalStore(controller?.subscribe ?? noSubscription, controller?.getSnapshot ?? (() => NO_READ));

  useEffect(() => {
    controller?.resume();
    return () => controller?.retire();
  }, [controller]);

  // A hidden tab reads nothing: its first read waits for the tab to come back.
  useEffect(() => {
    if (enabled && document.visibilityState !== 'hidden') controller?.refresh();
  }, [controller, enabled]);

  /* The one read loop of the live screens. Coming back to the tab refreshes one task later: Identity's own foreground
     check (app/use-identity-session.ts) freezes screen reads in that same event, so this read waits for it, and the
     reconnect that follows joins it instead of sending a second request. */
  const resume = useRef<number | undefined>(undefined);
  const liveRefresh = useMemo(() => ({
    tick: () => { controller?.refreshInBackground(pollIntervalMs); },
    presenceChanged: () => { window.clearTimeout(resume.current); },
    foreground: () => { resume.current = window.setTimeout(() => controller?.refreshInBackground(pollIntervalMs), 0); },
  }), [controller, pollIntervalMs]);
  useEffect(() => () => { window.clearTimeout(resume.current); }, [liveRefresh]);
  useLiveRefresh(liveRefresh, enabled ? pollIntervalMs : 0);

  const disabledReason = !authorised ? 'Tu sesión ha caducado. Vuelve a identificarte para continuar.'
    : connection !== 'online' ? 'Reconectando. Los cambios esperan una lectura confirmada.' : undefined;
  const inbox = projection(read, authorised, enabled);
  const canLoadOlder = enabled && inbox.status === 'ready' && !inbox.complete && read.pages < NOTICE_INBOX_MAX_PAGES;
  const presentation = {
    // The Backend's exact total of the whole inbox, never a count of the notices read.
    knownUnreadCount: inbox.unreadCount,
    content: controller === null
      ? <div className="notice-list notice-list--dialog"><NoticeList inbox={inbox} now={now()} {...(disabledReason ? { disabledReason } : {})} /></div>
      : <InboxPresence controller={controller}><div className="notice-list notice-list--dialog"><NoticeList
        now={now()} inbox={inbox}
        {...(canLoadOlder ? { onLoadOlder: controller.loadOlder } : {})}
        onNavigate={onNavigateHome === undefined ? undefined : () => onNavigateHome()}
        onReceipt={(id, action) => { void controller.record([id], action); }}
        onReceiptMany={(ids, action) => { void controller.record(ids, action); }}
        {...(enabled ? { onRetry: controller.refresh } : {})}
        {...(disabledReason ? { disabledReason } : {})}
        pendingNotices={read.pending} receiptErrors={read.errors} /></div></InboxPresence>,
  };
  const staffContext = useMemo(() => controller === null ? null : { controller, enabled }, [controller, enabled]);

  return <SessionInboxContext.Provider value={staffContext}>
    <PanelSessionInboxPresentationProvider value={presentation}>{children}</PanelSessionInboxPresentationProvider>
  </SessionInboxContext.Provider>;
}
